/**
 * Exports an ISA Datasheet Package as one merged PDF.
 *
 * Cloned from dsPdfExporter rather than generalised: that one is the Composer's single-sheet
 * export and is reached from a locked-adjacent path, so it stays as it is.
 *
 * The save picker is opened BEFORE the render, while the user's click is still the active
 * gesture — showSaveFilePicker is refused if you ask for it after an await chain.
 *
 * `indexSheetReports` (ReportPdfDocument props keyed by index sheet ID) interleaves each index
 * sheet's report ahead of its datasheets; see DsPackagePdfDocument. It is posted to the worker, so
 * it must be plain data.
 */
const exportDsPackagePdf = async ({ sheets, watermark = null, metadata = {}, indexSheetReports = null, onProgress }) => {
  try {
    const pkg = metadata.dsPackage || 'package';
    const rev = metadata.dsPackageRev ? `-Rev${metadata.dsPackageRev}` : '';
    const suffix = metadata.partial ? '-partial' : '';
    const filename = `ISA-Datasheets-${pkg}${rev}${suffix}.pdf`.replace(/[/\\?%*:|"<>]/g, '-');

    if (onProgress) onProgress(`Rendering ${sheets.length} datasheet${sheets.length === 1 ? '' : 's'}…`);

    let fileHandle = null;
    if (window.showSaveFilePicker) {
      try {
        fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }],
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          if (onProgress) onProgress(null);
          return false;
        }
      }
    }

    const blob = await new Promise((resolve, reject) => {
      const worker = new Worker(new URL('../../workers/dsPackagePdfWorker.js', import.meta.url));
      worker.onmessage = ({ data: result }) => {
        worker.terminate();
        if (result.type === 'done') resolve(result.blob);
        else reject(new Error(result.message || 'PDF worker failed'));
      };
      worker.onerror = (err) => { worker.terminate(); reject(err); };
      worker.postMessage({ sheets, watermark, metadata, indexSheetReports });
    });

    if (fileHandle) {
      try {
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } catch { fileHandle = null; }
    }

    if (!fileHandle) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    if (onProgress) onProgress(null);
    return true;
  } catch (err) {
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportDsPackagePdf;
